import { Fragment, useEffect, useState, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useModal } from 'src/utils/hooks';
import { State } from 'src/types';
import Head, { Seo } from 'src/utils/head';
import { throttle } from 'lodash';
import i18n from 'src/utils/translate';

import { updateLanguage, resetFloatingSignupBox, fetchGuidelinesOnce } from 'src/modules/shared/context/actions';

import Alert from 'src/typescript/components/alert/alert';
import Animate from 'src/components/animate';
import CommunityHeader from 'src/modules/community/shared/components/header/communityHeader';
import ESocialPrescriptionCta from 'src/components/ESocialPrescriptionCta';
import FloatingSignupBox from 'src/components/floatingSignupBox';
import Footer from 'src/components/footer';
import Loader from 'src/components/loader';
import Modal from 'src/modules/shared/modal/components/modalRoot';
import SiteBar from 'src/components/siteBar/siteBar';
import VerificationBar from 'src/components/verificationBar';

import dynamic from 'next/dynamic';
const CommunityGuidelinesModal = dynamic(() => import('src/modules/community/shared/components/communityGuidelinesModal'), { ssr: false });
const CommunityInvitesModal = dynamic(() => import('src/modules/shared/modal/components/communityInvites/communityInvitesModal'), { ssr: false });
const ContactAdminsModal = dynamic(() => import('src/modules/shared/modal/components/contactAdminsModal'), { ssr: false });
const EditBadgesModal = dynamic(() => import('src/modules/shared/modal/components/editBadges/editBadgesModal'), { ssr: false });
const MarketingOptinModal = dynamic(() => import('src/modules/shared/modal/components/marketingOptinModal'), { ssr: false });
const ProfileSurveyModal = dynamic(() => import('src/modules/profileSurvey/components/profileSurveyModal'), { ssr: false });
const TakeProfileSurveyModal = dynamic(() => import('src/modules/profileSurvey/components/takeProfileSurveyModal'), {ssr: false});
const TakeProfileSurveyOnSignupModal = dynamic(() => import('src/modules/shared/modal/components/signup/profileSurveyOnSignup'), {ssr: false});

type CommunityLayout = {
  children?: ReactNode;
  containerClassName?: string;
  currentPage?: string;
  seo?: Seo;
  sideBarContent?: ReactNode;
};

export default function CommunityLayout({
  children,
  containerClassName,
  currentPage,
  seo,
  sideBarContent,
}: CommunityLayout) {
  const dispatch = useDispatch();

  const { currentUser, currentCommunity, showVerificationBar, isFloatingSignupBoxVisible } = useSelector(({context}: State) => context);

  // Fetch community guidelines
  useEffect(() => {
    dispatch(fetchGuidelinesOnce(currentCommunity?.slug, currentCommunity?.isPrivate));
  }, [currentCommunity?.slug, currentCommunity?.isPrivate]);

  // set up scroll listener
  const [showScroll, setShowScroll] = useState(false);
  const [showFloatingSignupBox, setShowFloatingSignupBox] = useState(false);
  useEffect(() => {
    if (!isFloatingSignupBoxVisible) {
      dispatch(resetFloatingSignupBox());
    }
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const onScroll = throttle(() => {
    if (window.scrollY > 0) {
      setShowScroll(true);
      setShowFloatingSignupBox(true);
    } else {
      setShowScroll(false);
    }
  }, 300);

  return (
    <Fragment>
      {currentCommunity && <Head {...seo}/>}
      <SiteBar />
      {currentUser && showVerificationBar && <VerificationBar />}
      <ESocialPrescriptionCta />
      <Alert />
      {!currentCommunity
        ? <Loader />
        : (
          <main className="content-top-margin" role="main">
            <h1 className="sr-only">{seo?.title || 'HealthUnlocked | The social network for health'}</h1>
            <CommunityHeader currentPage={currentPage} />
            <div className={`results community-page-container ${containerClassName ? containerClassName : ''}`}>
              <div className={containerClassName === 'full-width' ? '' : 'results__content'}>
                {children}
              </div>
              {containerClassName !== 'full-width' &&
                <div className="results__sidebar">
                  {sideBarContent}
                </div>
              }
            </div>
          </main>
        )
      }
      <Animate classNames="TRANSITION-simpleFade" show={showScroll}>
        <button
          className={'back-to-top back-to-top--navbar icon-up-open'}
          title={i18n.t('Back to top')}
          type="button"
          onClick={() => window.scrollTo(0, 0)}
        />
      </Animate>
      <Footer isLoggedIn={!!currentUser} updateLanguage={(lang: string) => dispatch(updateLanguage(lang))} />
      { // StickyFeaturedPost overwrites FloatingSignupBox for visitors on the single post page
        !currentUser && isFloatingSignupBoxVisible &&
          <FloatingSignupBox communitySlug={currentCommunity?.slug} show={showFloatingSignupBox} />
      }
      <Modal />
      {useModal('COMMUNITY_GUIDELINES', <CommunityGuidelinesModal />)}
      {useModal('COMMUNITY_INVITES', <CommunityInvitesModal />)}
      {useModal('CONTACT_ADMINS', <ContactAdminsModal />)}
      {useModal('EDIT_BADGES', <EditBadgesModal />)}
      {useModal('MARKETING_OPT_IN', <MarketingOptinModal />)}
      {useModal('PROFILE_SURVEY', <ProfileSurveyModal />)}
      {useModal('TAKE_PROFILE_SURVEY', <TakeProfileSurveyModal />)}
      {useModal('TAKE_PROFILE_SURVEY_ON_SIGNUP', <TakeProfileSurveyOnSignupModal />)}
    </Fragment>
  );
}
